import React, { useContext, useState } from 'react';
// @ts-ignore
import nestedProperty from 'nested-property';
import { Link } from 'react-router-dom';
import {
  EditItem,
  EditSection,
  LoadingContainer,
} from '../../components/Layout';
import { IconButton } from '../../components/Button/Button';
import {
  FaArrowDown,
  FaArrowUp,
  FaExternalLinkAlt,
  FaFilePdf,
  FaPlus,
  FaSave,
  FaTrash,
} from 'react-icons/all';
import i18n from '@/utils/i18n';
import { useMutation, useQuery } from '@apollo/client';
import { updateStoreSuperAdminMutation } from '../../graphql/mutations';
import {
  CanAccessPartnerData,
  ViewerContext,
} from '../../providers/ViewerContextProvider';
import { adminInfoStoreQuery } from '../../graphql/queries';
import { DateTimeHelpers } from '../../tools/DateTimeHelpers';
import { getObjectDiff } from '@/utils/objDiff';
import { Dropdown } from 'primereact/dropdown';
import { ContentSection } from '../../components/Layout/ContentSection';

type MerchantMenuTabProps = {
  externalPickpackId: string | null;
  title: string;
};

export const MerchantMenuTab: React.FC<MerchantMenuTabProps> = ({
  externalPickpackId,
  title,
}) => {
  const [labelColorOptions, setLabelColorOptions] = useState<any>({
    1: { textColor: '#000000', color: '#f0de53' },
    3: { textColor: '#000000', color: '#1cc282' },
  });

  const [storeUpdate, setStoreUpdate] = useState<any | undefined>(null);
  const [oldStoreUpdate, setOldStoreUpdate] = useState<any | undefined>(null);
  const [updateStoreSuperAdmin, { loading: updatingSuperAdminInfo }] =
    useMutation(updateStoreSuperAdminMutation);

  const viewer = useContext(ViewerContext);

  const allowedToAccess = CanAccessPartnerData(viewer);

  function resetStore(storeIn: any) {
    const localLabelColorOptions = labelColorOptions;
    const { labels } = storeIn;
    let keys = Object.keys(localLabelColorOptions);
    for (const label of labels || []) {
      for (const key of keys) {
        if (
          toLower(localLabelColorOptions[key].textColor) ===
            toLower(label.text_color) &&
          toLower(localLabelColorOptions[key].color) === toLower(label.color)
        ) {
          label.key = key;
          break;
        }
      }
      if (label.key == null) {
        const newKey = (parseInt(keys[keys.length - 1]) + 1).toString();
        label.key = newKey;
        localLabelColorOptions[newKey] = {
          textColor: label.text_color,
          color: label.color,
        };
        keys = Object.keys(localLabelColorOptions);
      }
    }
    setLabelColorOptions(localLabelColorOptions);
    setStoreUpdate(storeIn);
    setOldStoreUpdate(JSON.parse(JSON.stringify(storeIn)));
  }

  const { data = { store: null }, loading: loadingStore } = useQuery(
    adminInfoStoreQuery,
    {
      fetchPolicy: 'no-cache',
      skip: !externalPickpackId,
      variables: { id: externalPickpackId },
      onCompleted: (storeData) => {
        // @ts-ignore
        if (storeData) {
          resetStore(storeData.store);
        }
      },
    },
  );

  if (loadingStore || !storeUpdate) {
    return <LoadingContainer />;
  }

  const store: any = data.store;

  function toLower(text: string) {
    return (text || '').toLowerCase();
  }

  // @ts-ignore
  function handleChange({ target }) {
    const { name, value } = target;
    const isNumber = target.hasAttribute('data-isnumber');
    const isDate = target.hasAttribute('data-isdate');
    let formattedValue = value;
    if (isNumber) formattedValue = Number(value);
    if (isDate) {
      const date = (target as EventTarget & HTMLInputElement).valueAsDate;
      if (date != null) {
        formattedValue = DateTimeHelpers.toDateTimeOffsetString(date);
      } else {
        formattedValue = DateTimeHelpers.toDateTimeOffsetString(new Date(0));
      }
    }

    setStoreUpdate((oldStoreUpdate: any) => {
      let newStoreUpdate = { ...oldStoreUpdate };
      nestedProperty.set(newStoreUpdate, name, formattedValue);
      return newStoreUpdate;
    });
  }

  function handleSaveSuperAdmin() {
    const getMutationInput = (storeIn: any) => ({
      type: storeIn.type,
      invoice_headline: storeIn.invoice_headline,
      onboarding_fee_vytal: isNaN(parseInt(storeIn.onboarding_fee_vytal))
        ? null
        : parseInt(storeIn.onboarding_fee_vytal),
      invoice_email: (storeIn.invoice_email || '').trim() || null,
      legal_name: (storeIn.legal_name || '').trim() || null,
      invoice_address: {
        ...(storeIn.invoice_address || {}),
        __typename: undefined,
      },
      custom_invoice_items: storeIn.custom_invoice_items
        ? storeIn.custom_invoice_items.map((item: object) => ({
            ...item,
            __typename: undefined,
          }))
        : null,
      contracted_date: storeIn.contracted_date,
      onboarded_date: storeIn.onboarded_date,
      test_period_end: storeIn.test_period_end,
      coming_soon: storeIn.coming_soon,
      temporarily_unavailable: storeIn.temporarily_unavailable,
      published: storeIn.published,
      can_preorder: storeIn.can_preorder,
      can_deliver: storeIn.can_deliver,
      always_online: storeIn.always_online,
      phone_from_vytal: storeIn.phone_from_vytal,
      sim_info: storeIn.sim_info,
      sim_phone_number: storeIn.sim_phone_number,
      sim_payment_automation: storeIn.sim_payment_automation,
      show_in_stats: storeIn.show_in_stats,
      region: parseInt(storeIn.region),
      store_wide_discount_percent: parseInt(
        storeIn.store_wide_discount_percent,
      ),
      labels: (storeIn.labels || []).map((l: any) => {
        return { text: l.text, color: l.color, text_color: l.text_color };
      }),
    });
    const mutationInput = getObjectDiff(
      getMutationInput(oldStoreUpdate),
      getMutationInput(storeUpdate),
    );
    console.log('mutationInput admin', mutationInput);

    updateStoreSuperAdmin({
      variables: {
        id: store.id,
        store: mutationInput,
      },
      update: (cache, { data: { updateStoreSuperAdmin: updatedStore } }) => {
        resetStore(updatedStore);
      },
    }).catch((err: any) => {
      alert('Error updating store as super admin: ' + err);
    });
  }

  function toNumber(value: string) {
    const num = parseInt(value);
    if (isNaN(num)) {
      return 0;
    } else {
      return num;
    }
  }

  function swapLabels(from: number, to: number) {
    setStoreUpdate((oldStoreUpdate: any) => {
      const newStoreUpdate = { ...oldStoreUpdate };
      const labels: any[] = newStoreUpdate.labels || [];
      if (to >= 0 && to < labels.length) {
        var tmp = labels[from];
        labels[from] = labels[to];
        labels[to] = tmp;
      }
      newStoreUpdate.labels = labels;
      return newStoreUpdate;
    });
  }

  function addStoreLabel() {
    setStoreUpdate((oldStoreUpdate: any) => {
      const newStoreUpdate = { ...oldStoreUpdate };
      const labels: any[] = newStoreUpdate.labels || [];
      labels.push({ text: '', color: '#F0DE53' });
      newStoreUpdate.labels = labels;
      return newStoreUpdate;
    });
  }

  function removeLabel(index: number) {
    setStoreUpdate((oldStoreUpdate: any) => {
      const newStoreUpdate = { ...oldStoreUpdate };
      const labels: any[] = newStoreUpdate.labels || [];
      if (labels.length >= index) {
        labels.splice(index, 1);
      }
      newStoreUpdate.labels = labels;
      return newStoreUpdate;
    });
  }

  function setStoreLabel(index: number, value: string, key: string) {
    setStoreUpdate((oldStoreUpdate: any) => {
      let newStoreUpdate = { ...oldStoreUpdate };
      let labels: any[] = newStoreUpdate.labels || [];
      if (labels.length <= index) {
        labels = labels.concat(
          Array.from({ length: 1 + index - labels.length }, (_, i) => {
            return { text: '', color: '#000000' };
          }),
        );
      }
      if (key === 'colors') {
        labels[index].key = value;
        labels[index].color = labelColorOptions[value].color;
        labels[index].text_color = labelColorOptions[value].textColor;
      } else {
        labels[index][key] = value;
      }
      newStoreUpdate.labels = labels;
      return newStoreUpdate;
    });
  }

  function optionItemTemplate(option: any) {
    return (
      <span
        style={{
          color: labelColorOptions[option.value].textColor || '#000000',
          backgroundColor: labelColorOptions[option.value].color,
          padding: '0.2em',
        }}
      >
        {option.label || ''}
      </span>
    );
  }

  function selectedOptionTemplate(option: any, props: any) {
    if (option) {
      return (
        <span
          style={{
            color: labelColorOptions[option.value].textColor || '#000000',
            backgroundColor: labelColorOptions[option.value].color,
            padding: '0.2em',
          }}
        >
          {option.label || ''}
        </span>
      );
    }

    return <span>{props.placeholder}</span>;
  }

  function renderStoreLabels() {
    const labels = (storeUpdate.labels || []).map((l: any, index: number) => (
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginLeft: '1em',
          marginRight: '1em',
          marginTop: '0.75em',
        }}
      >
        <div className="input-group" style={{ width: '60%' }}>
          <span className="input-group-text">
            {' '}
            {`${index + 1}. ${i18n.t('Menu:label')}`}
          </span>
          <input
            type="text"
            className="form-control"
            lang="de-DE"
            onChange={(e) => setStoreLabel(index, e.target.value, 'text')}
            value={l.text || ''}
            style={{
              color: l.text_color || '#000000',
              backgroundColor: l.color,
              height: '100%',
            }}
          />
          <Dropdown
            value={l.key}
            options={Object.keys(labelColorOptions).map((k) => ({
              label: l.text || '',
              value: k,
            }))}
            onChange={(e) => setStoreLabel(index, e.value, 'colors')}
            placeholder="Select a color schema"
            itemTemplate={optionItemTemplate}
            valueTemplate={selectedOptionTemplate}
          />
        </div>
        <div>
          <button
            onClick={() => swapLabels(index, index - 1)}
            style={{
              border: '#00000000',
              background: '#00000000',
              marginRight: '1em',
            }}
          >
            <FaArrowUp />
          </button>
          <button
            onClick={() => swapLabels(index, index + 1)}
            style={{
              border: '#00000000',
              background: '#00000000',
              marginRight: '1em',
            }}
          >
            <FaArrowDown />
          </button>
          <button
            onClick={() => removeLabel(index)}
            style={{ border: '#00000000', background: '#00000000' }}
          >
            <FaTrash />
          </button>
        </div>
      </div>
    ));
    return labels;
  }

  if (!externalPickpackId) return <p>externalPickpackId is not avaliable</p>;

  if (!store) return <p>Store {externalPickpackId} could not be loaded</p>;

  return (
    <ContentSection title={title} collapsable={false} key="content">
      <EditSection title={i18n.t('Menu:menuForPreorders')}>
        <EditItem title={i18n.t('Menu:quickLinks')}>
          <div className="d-flex flex-column align-items-start">
            <IconButton
              className="mb-2"
              label={i18n.t('Menu:menuForPreorders')}
              icon={<FaExternalLinkAlt />}
              tag={Link}
              to={'/de/store/' + externalPickpackId}
              target="_blank"
            />
            <IconButton
              className="mt-2"
              label={i18n.t('Menu:guidelineForCreatingMenu')}
              icon={<FaFilePdf />}
              tag="a"
              href="https://drive.google.com/file/d/1W_dhJzWzjHFH3924reGZDq2L1SQ_fE-K/view?usp=sharing"
              target="_blank"
            />
          </div>
        </EditItem>
        {allowedToAccess && (
          <div>
            <EditItem
              title={i18n.t('Menu:discount')}
              helpText={i18n.t('Menu:discountHelpText')}
              lockIcon={true}
            >
              <div className="input-group">
                <input
                  type="number"
                  className={
                    'form-control ' +
                    (isNaN(parseInt(storeUpdate.store_wide_discount_percent))
                      ? 'is-invalid'
                      : '')
                  }
                  name="store_wide_discount_percent"
                  autoComplete="off"
                  lang="en-US"
                  onChange={(e) => handleChange(e)}
                  step="1"
                  value={storeUpdate.store_wide_discount_percent || ''}
                  placeholder="0"
                />
                <span className="input-group-text">
                  {' '}
                  {`${toNumber(storeUpdate.store_wide_discount_percent)} %`}
                </span>
              </div>
            </EditItem>
            <EditItem
              title={i18n.t('Menu:label')}
              helpText={i18n.t('Menu:labelHelpText')}
              lockIcon={true}
            >
              {renderStoreLabels()}
              <br />
              <IconButton
                className="mt-2"
                label={i18n.t('Menu:addStoreLabel')}
                icon={<FaPlus />}
                onClick={addStoreLabel}
              />
            </EditItem>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <IconButton
                className="mt-2"
                label={i18n.t('Common:save')}
                icon={<FaSave />}
                onClick={handleSaveSuperAdmin}
                isLoading={updatingSuperAdminInfo}
              />
            </div>
          </div>
        )}
      </EditSection>
    </ContentSection>
  );
};
